import { useEffect, useState } from "react";
import { Ticket, Search, CheckCircle2, XCircle, ShieldCheck, Clock, Percent, PlusCircle, Copy } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const STATUS_META = {
  valid: { label: "Valide", cls: "bg-green-100 text-green-800 border-green-200" },
  used: { label: "Utilisé", cls: "bg-stone/40 text-graphite border-stone" },
  expired: { label: "Expiré", cls: "bg-red-100 text-red-700 border-red-200" },
};

const REASON_LABELS = {
  not_found: "Aucun coupon avec ce code.",
  email_mismatch: "Cet email ne correspond pas au titulaire du coupon.",
  used: "Ce coupon a déjà été utilisé.",
  expired: "Ce coupon a expiré.",
};

const Lbl = ({ children }) => <label className="text-xs uppercase tracking-[0.15em] text-ash">{children}</label>;

export default function SuperLoyaltyCoupons() {
  const { user } = useAuth();
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [checking, setChecking] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [result, setResult] = useState(null); // { valid, reason, coupon }

  const [coupons, setCoupons] = useState(null);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const [genQuery, setGenQuery] = useState("");
  const [genResults, setGenResults] = useState([]);
  const [searchingClient, setSearchingClient] = useState(false);
  const [genSelected, setGenSelected] = useState(null); // { id, name, email, phone }
  const [genType, setGenType] = useState("percent");
  const [genValue, setGenValue] = useState("10");
  const [genDays, setGenDays] = useState("14");
  const [genPrefix, setGenPrefix] = useState("PROMO");
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null);

  const loadList = () => {
    api.get("/superadmin/loyalty/coupons", {
      params: {
        search: search || undefined,
        status: statusFilter || undefined,
        discount_type: typeFilter || undefined,
        page, page_size: PAGE_SIZE,
      },
    })
      .then(({ data }) => { setCoupons(data.items); setTotal(data.total); })
      .catch(() => {});
  };
  useEffect(() => { loadList(); }, [search, statusFilter, typeFilter, page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, [search, statusFilter, typeFilter]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Recherche flexible du client (nom, email ou téléphone) avant toute
  // génération de coupon, pour garantir qu'on cible un compte existant.
  useEffect(() => {
    const q = genQuery.trim();
    if (q.length < 2) {
      setGenResults([]);
      return;
    }
    setSearchingClient(true);
    const t = setTimeout(() => {
      api.get("/superadmin/loyalty/customers/search", { params: { q } })
        .then(({ data }) => setGenResults(Array.isArray(data) ? data : []))
        .catch(() => setGenResults([]))
        .finally(() => setSearchingClient(false));
    }, 300);
    return () => clearTimeout(t);
  }, [genQuery]);

  if (user?.role !== "superadmin") return <p className="text-graphite">Accès réservé au super administrateur.</p>;

  const verify = async (e) => {
    e.preventDefault();
    if (!code.trim() || !email.trim()) {
      toast.error("Code et email sont requis.");
      return;
    }
    setChecking(true);
    setResult(null);
    try {
      const { data } = await api.post("/superadmin/loyalty/coupons/verify", { code: code.trim(), email: email.trim() });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setChecking(false);
    }
  };

  const redeem = async () => {
    if (!window.confirm(`Confirmer l'utilisation du coupon ${result.coupon.code} ? Cette action est définitive.`)) return;
    setRedeeming(true);
    try {
      const { data } = await api.post("/superadmin/loyalty/coupons/redeem", { code: code.trim(), email: email.trim() });
      setResult({ valid: false, justRedeemed: true, coupon: data.coupon });
      toast.success("Coupon validé et consommé.");
      setCode("");
      setEmail("");
      loadList();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setRedeeming(false);
    }
  };

  const generate = async (e) => {
    e.preventDefault();
    if (!genSelected) {
      toast.error("Recherchez et sélectionnez d'abord le client (par email ou téléphone).");
      return;
    }
    if (!genValue) {
      toast.error("La valeur de la réduction est requise.");
      return;
    }
    setGenerating(true);
    setGenerated(null);
    try {
      const { data } = await api.post("/superadmin/loyalty/coupons", {
        email: genSelected.email,
        discount_type: genType,
        discount_value: parseFloat(genValue),
        validity_days: parseInt(genDays, 10) || 14,
        prefix: genPrefix.trim() || "PROMO",
      });
      setGenerated(data);
      toast.success(`Coupon ${data.code} généré.`);
      setGenSelected(null);
      setGenQuery("");
      loadList();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const copyCode = (value) => {
    navigator.clipboard?.writeText(value);
    toast.success("Code copié.");
  };

  return (
    <div data-testid="loyalty-coupons">
      <p className="text-xs uppercase tracking-[0.2em] text-ash mb-2 flex items-center gap-2">
        <Ticket className="h-4 w-4 text-gold" /> Fidélité
      </p>
      <h1 className="font-serif text-4xl md:text-5xl text-ink tracking-tight mb-8">Coupons</h1>

      {/* Génération manuelle */}
      <div className="bg-white border border-stone rounded-sm p-6 mb-8">
        <h2 className="font-serif text-2xl text-ink mb-1 flex items-center gap-2"><PlusCircle className="h-5 w-5 text-copper" /> Générer un coupon pour un client</h2>
        <p className="text-graphite text-sm mb-5">Créez un code promo ponctuel pour un client précis, en dehors de toute campagne.</p>

        <form onSubmit={generate} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="sm:col-span-2 lg:col-span-1 relative">
            <Lbl>Client (nom, email ou téléphone)</Lbl>
            {genSelected ? (
              <div className="mt-1.5 h-9 flex items-center justify-between gap-2 border border-stone rounded-sm bg-sand/40 px-3">
                <p className="text-sm text-ink truncate">
                  <span className="font-medium">{genSelected.name || genSelected.email}</span>
                  <span className="text-graphite"> · {genSelected.email}{genSelected.phone ? ` · ${genSelected.phone}` : ""}</span>
                </p>
                <button type="button" data-testid="coupon-gen-client-clear" onClick={() => setGenSelected(null)} className="text-ash hover:text-ink shrink-0">
                  <XCircle className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <>
                <Input
                  data-testid="coupon-gen-search-input" value={genQuery} onChange={(e) => setGenQuery(e.target.value)}
                  className="mt-1.5 rounded-sm border-stone bg-white" placeholder="Nom, email ou téléphone du client…"
                />
                {genQuery.trim().length >= 2 && (
                  <div data-testid="coupon-gen-results" className="absolute z-10 mt-1 w-full bg-white border border-stone rounded-sm shadow-md max-h-56 overflow-y-auto">
                    {searchingClient ? (
                      <p className="px-3 py-2 text-sm text-graphite">Recherche…</p>
                    ) : genResults.length === 0 ? (
                      <p className="px-3 py-2 text-sm text-graphite">Aucun client trouvé.</p>
                    ) : (
                      genResults.map((c) => (
                        <button
                          type="button" key={c.id} data-testid={`coupon-gen-result-${c.id}`}
                          onClick={() => { setGenSelected(c); setGenQuery(""); setGenResults([]); }}
                          className="w-full text-left px-3 py-2 hover:bg-sand/60 text-sm border-b border-stone last:border-0"
                        >
                          <p className="text-ink font-medium truncate">{c.name || c.email}</p>
                          <p className="text-xs text-graphite truncate">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          <div>
            <Lbl>Type de réduction</Lbl>
            <select data-testid="coupon-gen-type" value={genType} onChange={(e) => setGenType(e.target.value)} className="mt-1.5 w-full border border-stone rounded-sm px-3 py-2 text-sm bg-white">
              <option value="percent">Pourcentage (%)</option>
              <option value="amount">Montant fixe (€)</option>
            </select>
          </div>
          <div>
            <Lbl>Valeur</Lbl>
            <Input data-testid="coupon-gen-value" type="number" min="0" step="0.01" value={genValue} onChange={(e) => setGenValue(e.target.value)} className="mt-1.5 rounded-sm border-stone bg-white" />
          </div>
          <div>
            <Lbl>Validité (jours)</Lbl>
            <Input data-testid="coupon-gen-days" type="number" min="1" max="365" value={genDays} onChange={(e) => setGenDays(e.target.value)} className="mt-1.5 rounded-sm border-stone bg-white" />
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <Lbl>Préfixe du code (optionnel)</Lbl>
            <Input data-testid="coupon-gen-prefix" value={genPrefix} onChange={(e) => setGenPrefix(e.target.value)} className="mt-1.5 rounded-sm border-stone bg-white uppercase" placeholder="PROMO" />
          </div>
          <div className="sm:col-span-2 lg:col-span-4">
            <button type="submit" data-testid="coupon-gen-btn" disabled={generating || !genSelected} className="flex items-center gap-2 bg-ink text-cream px-5 py-3 text-xs uppercase tracking-[0.15em] hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
              <PlusCircle className="h-4 w-4" /> {generating ? "Génération…" : "Générer le coupon"}
            </button>
          </div>
        </form>

        {generated && (
          <div data-testid="coupon-gen-result" className="mt-5 border border-green-200 bg-green-50 rounded-sm p-4">
            <p className="flex items-center gap-2 text-green-800 font-medium mb-3"><CheckCircle2 className="h-5 w-5" /> Coupon créé pour {generated.user_email}</p>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono text-lg text-ink bg-white border border-stone px-3 py-1.5 rounded-sm">{generated.code}</span>
              <button type="button" onClick={() => copyCode(generated.code)} className="flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] text-graphite hover:text-ink">
                <Copy className="h-3.5 w-3.5" /> Copier
              </button>
              <span className="text-sm text-graphite flex items-center gap-1"><Percent className="h-3.5 w-3.5 text-ash" /> {generated.discount_value}{generated.discount_type === "percent" ? "%" : " €"}</span>
              <span className="text-sm text-graphite flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-ash" /> Expire le {new Date(generated.expires_at).toLocaleDateString("fr-FR")}</span>
            </div>
          </div>
        )}
      </div>

      {/* Validation en magasin */}
      <div className="bg-white border border-stone rounded-sm p-6 mb-8">
        <h2 className="font-serif text-2xl text-ink mb-1 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-copper" /> Valider un coupon en magasin</h2>
        <p className="text-graphite text-sm mb-5">Le code et l&apos;email du client doivent tous les deux correspondre avant toute consommation.</p>

        <form onSubmit={verify} className="grid sm:grid-cols-2 gap-4">
          <div>
            <Lbl>Code du coupon</Lbl>
            <Input data-testid="coupon-code-input" value={code} onChange={(e) => setCode(e.target.value)} className="mt-1.5 rounded-sm border-stone bg-white uppercase" placeholder="PROMO-A1B2C3" />
          </div>
          <div>
            <Lbl>Email du client</Lbl>
            <Input data-testid="coupon-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 rounded-sm border-stone bg-white" placeholder="client@email.com" />
          </div>
          <div className="sm:col-span-2">
            <button type="submit" data-testid="coupon-verify-btn" disabled={checking} className="flex items-center gap-2 bg-ink text-cream px-5 py-3 text-xs uppercase tracking-[0.15em] hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
              <Search className="h-4 w-4" /> {checking ? "Vérification…" : "Vérifier"}
            </button>
          </div>
        </form>

        {result && (
          <div data-testid="coupon-verify-result" className={`mt-5 border rounded-sm p-4 ${result.valid || result.justRedeemed ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
            {result.justRedeemed ? (
              <p className="flex items-center gap-2 text-green-800 font-medium">
                <CheckCircle2 className="h-5 w-5" /> Coupon {result.coupon.code} consommé avec succès — il ne pourra plus être réutilisé.
              </p>
            ) : result.valid ? (
              <>
                <p className="flex items-center gap-2 text-green-800 font-medium"><CheckCircle2 className="h-5 w-5" /> Coupon valide</p>
                <div className="mt-3 grid sm:grid-cols-3 gap-3 text-sm text-ink">
                  <p><span className="text-ash">Client : </span>{result.coupon.user_email}</p>
                  <p className="flex items-center gap-1"><Percent className="h-3.5 w-3.5 text-ash" /> {result.coupon.discount_value}{result.coupon.discount_type === "percent" ? "%" : " €"} de réduction</p>
                  <p className="flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-ash" /> Expire le {new Date(result.coupon.expires_at).toLocaleDateString("fr-FR")}</p>
                </div>
                <button
                  data-testid="coupon-redeem-btn" onClick={redeem} disabled={redeeming}
                  className="mt-4 bg-ink text-cream px-5 py-2.5 text-xs uppercase tracking-[0.15em] hover:bg-gold hover:text-ink transition-colors disabled:opacity-60"
                >
                  {redeeming ? "Validation…" : "Confirmer l'utilisation"}
                </button>
              </>
            ) : (
              <p className="flex items-center gap-2 text-red-700 font-medium">
                <XCircle className="h-5 w-5" /> {REASON_LABELS[result.reason] || "Ce coupon n'est pas utilisable."}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Liste des coupons */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-serif text-2xl text-ink">Tous les coupons</h2>
        <div className="flex flex-wrap gap-2">
          <Input data-testid="coupon-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher client ou code…" className="rounded-sm border-stone bg-white w-56" />
          <select data-testid="coupon-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-stone rounded-sm px-3 text-sm bg-white">
            <option value="">Tous les statuts</option>
            <option value="valid">Valides</option>
            <option value="used">Utilisés</option>
            <option value="expired">Expirés</option>
          </select>
          <select data-testid="coupon-type-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="border border-stone rounded-sm px-3 text-sm bg-white">
            <option value="">Tous les types</option>
            <option value="percent">Pourcentage (%)</option>
            <option value="amount">Montant fixe (€)</option>
          </select>
        </div>
      </div>

      {!coupons ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-14 bg-white border border-stone animate-pulse rounded-sm" />)}</div>
      ) : coupons.length === 0 ? (
        <p className="text-graphite">Aucun coupon.</p>
      ) : (
        <>
          <div className="bg-white border border-stone rounded-sm overflow-hidden overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="text-[11px] uppercase tracking-[0.12em] text-ash border-b border-stone bg-sand/40">
                <tr>
                  <th className="text-left px-4 py-3">Code</th>
                  <th className="text-left px-4 py-3">Client</th>
                  <th className="text-left px-4 py-3">Réduction</th>
                  <th className="text-left px-4 py-3">Expiration</th>
                  <th className="text-left px-4 py-3">Statut</th>
                  <th className="text-left px-4 py-3">Utilisé par</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((c) => (
                  <tr key={c.id} data-testid={`coupon-row-${c.code}`} className="border-b border-stone last:border-0">
                    <td className="px-4 py-3 font-mono text-ink">{c.code}</td>
                    <td className="px-4 py-3 text-graphite">{c.user_name || "—"}</td>
                    <td className="px-4 py-3 text-graphite">{c.discount_value}{c.discount_type === "percent" ? "%" : " €"}</td>
                    <td className="px-4 py-3 text-graphite">{new Date(c.expires_at).toLocaleDateString("fr-FR")}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] uppercase tracking-wide px-2 py-1 rounded-full border ${STATUS_META[c.status].cls}`}>{STATUS_META[c.status].label}</span>
                    </td>
                    <td className="px-4 py-3 text-graphite text-xs">{c.redeemed_by || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between gap-3 mt-4 text-sm text-graphite">
            <p>{total} coupon{total > 1 ? "s" : ""}</p>
            <div className="flex items-center gap-3">
              <button
                type="button" data-testid="coupon-page-prev" disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 border border-stone rounded-sm uppercase text-xs tracking-[0.12em] hover:bg-sand/60 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                Précédent
              </button>
              <span data-testid="coupon-page-indicator">Page {page} / {totalPages}</span>
              <button
                type="button" data-testid="coupon-page-next" disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-3 py-1.5 border border-stone rounded-sm uppercase text-xs tracking-[0.12em] hover:bg-sand/60 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                Suivant
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
